// ADC-IMPLEMENTS: <adc-006a-feature-card-01>
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  type Incident,
  SEVERITY_CONFIG,
  STATUS_CONFIG,
  formatIncidentAge,
} from '@/lib/incidentUtils';

interface IncidentCardProps {
  incident: Incident;
  connectionName?: string;
  className?: string;
}

export function IncidentCard({ incident, connectionName, className }: IncidentCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/incidents/${incident.connection_id}/${incident.conversation_id}/${incident.id}`);
  };

  return (
    <Card
      className={cn(
        "p-4 cursor-pointer hover:border-primary transition-colors",
        incident.severity === 'critical' && "border-red-600 border-l-4",
        incident.severity === 'high' && "border-red-500 border-l-4",
        incident.severity === 'medium' && "border-yellow-500 border-l-4",
        incident.severity === 'low' && "border-blue-500 border-l-4",
        className
      )}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={SEVERITY_CONFIG[incident.severity]?.className ?? SEVERITY_CONFIG.low.className}>
              {SEVERITY_CONFIG[incident.severity]?.label ?? 'Unknown'}
            </Badge>
            <Badge className={STATUS_CONFIG[incident.status]?.className ?? STATUS_CONFIG.PENDING.className}>
              {STATUS_CONFIG[incident.status]?.label ?? 'Unknown'}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate">
              {incident.context.conversation_name || 'Unknown Conversation'}
            </p>
            {connectionName && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal shrink-0">
                {connectionName}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
            {incident.narrative?.summary || incident.context.reasoning || 'Incident detected by automated analysis'}
          </p>
          {incident.context.flagged_reasons.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {incident.context.flagged_reasons.slice(0, 3).map((reason, i) => (
                <Badge key={i} variant="outline" className="text-xs">{reason}</Badge>
              ))}
              {incident.context.flagged_reasons.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{incident.context.flagged_reasons.length - 3} more
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
          <p>{formatIncidentAge(incident.created_at)}</p>
          <p className="mt-1">{incident.message_count} msg{incident.message_count !== 1 ? 's' : ''}</p>
          <p className="mt-1 font-mono text-[10px]">{incident.incident_id.slice(0, 8)}</p>
        </div>
      </div>
    </Card>
  );
}
